import { AppEnum } from '@aleph/shared-components';
import { useMemo } from 'react';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { UserAccountLevel } from 'wb-spotify/graphql/authgraph/generated';

/**
 * Documentation: https://alephholding.atlassian.net/wiki/spaces/TECHD/pages/1354826323/Roles+Permissions
 */
function useSpotifyRolePermissions() {
	const platformUser = useUserMetaData(AppEnum.SPOTIFY);

	const canCreateAdAccount = useMemo(
		() => [
			UserAccountLevel.AdvertiserAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.PlatformAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canEditAdAccount = useMemo(
		() => [
			UserAccountLevel.AdvertiserAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.PlatformAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canCreateAdvertiserAccount = useMemo(
		() => [
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.PlatformAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canDeleteAdvertiserAccount = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canEditAdvertiserAccount = useMemo(
		() => [
			UserAccountLevel.AdvertiserAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.PlatformAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canCreateReport = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
			UserAccountLevel.User,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canEditReport = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
			UserAccountLevel.User,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canShareReport = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
			UserAccountLevel.User,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canCreateTeam = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canEditTeam = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canDeleteTeam = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canCreateUser = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canEditUser = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	const canDeleteUser = useMemo(
		() => [
			UserAccountLevel.PlatformAdmin,
			UserAccountLevel.AgencyAdmin,
			UserAccountLevel.AdvertiserAdmin,
		].includes(platformUser?.userAccountLevel as UserAccountLevel),
		[platformUser?.userAccountLevel],
	);

	return {
		canCreateAdAccount,
		canEditAdAccount,
		canCreateAdvertiserAccount,
		canDeleteAdvertiserAccount,
		canEditAdvertiserAccount,
		canCreateReport,
		canEditReport,
		canShareReport,
		canCreateTeam,
		canEditTeam,
		canDeleteTeam,
		canCreateUser,
		canEditUser,
		canDeleteUser,
	};
}

export default useSpotifyRolePermissions;
